import dayjs from 'dayjs';
import { Dispatch, FC, MouseEventHandler, SetStateAction, useCallback, useRef, useState } from 'react';

import { getBookingByNumber, getBookingByUnitId } from 'services';
import { Booking, BookingDetails, Chassis, Container, CreatedBookingDetails, Genset, Role } from 'types';

import { TextField, TextFieldRef, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useAuth, useSelectedLocation } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import { useGateNewBooking } from '.';
import { useNewTransaction } from '..';

type BookingInputProps = {
  disabled?: boolean;
  required?: boolean;
  unit: Chassis | Container | Genset;
  setBooking: Dispatch<SetStateAction<Partial<Booking> | undefined | null>>;
  ['data-cy']?: string;
};

const BookingInput: FC<BookingInputProps> = ({ disabled, required, unit, setBooking, ...props }) => {
  const { userProfile } = useAuth();
  const { selectedLocation } = useSelectedLocation();
  const { transaction, container, chassis, genset, stack } = useNewTransaction();
  const { findNewBooking, setEditedBooking, setNewBooking, setPostProcessFn } = useGateNewBooking();
  const { newZonedDayJs } = useTimeZone();

  const [notFoundInput, setNotFoundInput] = useState('');
  const [maxQuantityBooking, setMaxQuantityBooking] = useState<Booking>();

  const bookingNumberRef = useRef<TextFieldRef>(null);

  const validateBooking = useCallback(
    async (bookingNumber: string): Promise<ValidationResult> => {
      const existingDraftBooking: Partial<Booking> | null =
        unit.booking?.bookingNumber === bookingNumber && !!unit.booking?.customer?.id && !!unit.booking?.location?.id
          ? unit.booking
          : null;
      /* use reservation if applicable */
      let booking: Booking | null = !unit?.id || unit.id < 0 ? null : await getBookingByUnitId(unit.id);
      /* verify booking number is present */
      if (!booking && !existingDraftBooking && !bookingNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (!unit.customer?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
      } else if (!selectedLocation?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
      }

      /* search bookings created--or modified--during this transaction before fetching */
      booking = findNewBooking(bookingNumber, unit.customer.id, selectedLocation.id) || booking;
      if (!booking) {
        booking = await getBookingByNumber(bookingNumber, unit.customer.id, selectedLocation.id);
      }
      const validatedBooking: Booking | Partial<Booking> | null = booking ?? existingDraftBooking;

      /* verify booking exists */
      if (!validatedBooking) {
        setNotFoundInput(bookingNumber);
        return {
          status: ValidationStatus.WARNING,
          error: `Booking not found for customer ${unit.customer.name ?? 'N/A'} and location ${
            selectedLocation.name ?? 'N/A'
          }`
        };
      } else setNotFoundInput('');

      /* verify booking is active */
      const expiration: dayjs.Dayjs = newZonedDayJs(validatedBooking.expirationDate).endOf('day');
      if (expiration.isBefore(newZonedDayJs(transaction.completedDate))) {
        return { status: ValidationStatus.WARNING, error: `Booking expired (${expiration.format('lll')})` };
      }
      const startDate: dayjs.Dayjs = newZonedDayJs(validatedBooking.startDate).startOf('day');
      if (startDate.isAfter(newZonedDayJs())) {
        return { status: ValidationStatus.WARNING, error: `Booking is not valid until ${startDate.format('lll')}` };
      }

      /* verify booking details */
      if (unit.sizeType) {
        const sizeTypeDetails: BookingDetails | undefined = validatedBooking.bookingDetails?.find(({ iso }) =>
          iso?.includes(unit.sizeType ?? '')
        );
        /* verify container sizeType is present */
        if (!sizeTypeDetails) {
          return { status: ValidationStatus.WARNING, error: `Booking does not include ${unit.sizeType}` };
        }
        const matchingGateUnits = [container, chassis, genset, ...stack]
          .filter(u => !!u)
          .filter(
            u =>
              sizeTypeDetails.iso?.includes(u.sizeType ?? '') &&
              u.booking?.bookingNumber === validatedBooking?.bookingNumber &&
              u.customer?.id === validatedBooking?.customer?.id &&
              u.location?.id === validatedBooking?.location?.id
          );
        const currentUnitId: number | undefined = unit.id;
        const currentUnitAlreadyOnBooking: boolean =
          !!currentUnitId && currentUnitId > 0 && !!validatedBooking.unitIds?.some(id => id === currentUnitId);
        const reservedCount = matchingGateUnits.filter(gateUnit => {
          if (!currentUnitAlreadyOnBooking || !currentUnitId || currentUnitId < 0) {
            return true;
          }
          return gateUnit.id !== currentUnitId;
        }).length;
        /* verify total quantity exceeds delivered count + reserved count (if unit not reserved) */
        if (
          !transaction.inbound &&
          reservedCount + (sizeTypeDetails.delivered ?? 0) > (sizeTypeDetails.count ?? 0)
        ) {
          setMaxQuantityBooking(booking ?? undefined);
          return {
            status: ValidationStatus.WARNING,
            error: `Booking quantity met (${sizeTypeDetails.count ?? 0} x ${unit.sizeType})`
          };
        }
      }

      /* booking validated */
      setMaxQuantityBooking(undefined);
      setNotFoundInput('');
      setBooking(validatedBooking);
      return { status: ValidationStatus.SUCCESS };
    },
    [
      unit.booking,
      unit.customer,
      unit.id,
      unit.sizeType,
      container,
      chassis,
      genset,
      stack,
      selectedLocation,
      transaction.inbound,
      transaction.completedDate,
      findNewBooking,
      newZonedDayJs,
      setNotFoundInput,
      setBooking
    ]
  );

  const editBookingQuantity: MouseEventHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (maxQuantityBooking) {
        setEditedBooking(maxQuantityBooking);
        setPostProcessFn(() => bookingNumberRef.current?.revalidate);
      }
    },
    [maxQuantityBooking, setPostProcessFn, setEditedBooking]
  );

  const createNewBooking: MouseEventHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      if (!selectedLocation) {
        bookingNumberRef.current?.setCustomValidity('No location selected');
      } else if (!unit.customer) {
        bookingNumberRef.current?.setCustomValidity('No account selected');
      } else if (!unit.sizeType) {
        bookingNumberRef.current?.setCustomValidity('No size-type selected');
      } else if (notFoundInput) {
        setNewBooking({
          bookingNumber: notFoundInput,
          bookingDetails: [new CreatedBookingDetails([unit.sizeType])],
          customer: unit.customer,
          expirationDate: newZonedDayJs().add(30, 'day').toISOString(),
          location: selectedLocation,
          notes: '',
          startDate: newZonedDayJs(transaction.completedDate ?? newZonedDayJs()).toISOString(),
          truckingCompanies: []
        });
        setPostProcessFn(() => bookingNumberRef.current?.revalidate);
      }
    },
    [
      notFoundInput,
      selectedLocation,
      transaction.completedDate,
      unit.customer,
      unit.sizeType,
      newZonedDayJs,
      setPostProcessFn,
      setNewBooking
    ]
  );

  return (
    <div className="d-flex g-2">
      <TextField
        disabled={disabled || unit.booking?.unitIds?.some(id => id === unit.id)}
        label="BOOKING"
        ref={bookingNumberRef}
        required={required}
        value={unit.booking?.bookingNumber ?? ''}
        onChange={bookingNumber => setBooking(!bookingNumber ? null : b => ({ ...b, bookingNumber }))}
        validate={validateBooking}
        data-cy={props['data-cy']}
      />
      {!(
        [
          Role.ADMIN,
          Role.BILLING_ADMIN,
          Role.SENIOR_BOOKING,
          Role.BOOKING,
          Role.GATE_BOOKING
        ].includes(userProfile.role)
      ) ? null : !!notFoundInput ? (
        <button className="btn-back text-primary" type="button" onClick={createNewBooking}>
          <span className="material-icons icon-large p-1">add_circle</span>
        </button>
      ) : !!maxQuantityBooking ? (
        <button className="btn-back text-primary" type="button" onClick={editBookingQuantity}>
          <span className="material-icons icon-large p-1">build_circle</span>
        </button>
      ) : null}
    </div>
  );
};

export default BookingInput;
